import type { ComuneFull } from '@/data';
import type { Filters } from './types';
import { F_DEFAULTS, matches } from './filter-fns';

// Какой именно фильтр сейчас активен (отличается от default)
export type ConstraintKey =
  | 'alt'
  | 't_jul_max'
  | 't_jan_min'
  | 'hum_max'
  | 'snow_min'
  | 'sunny_min'
  | 'zones'
  | 'pop_max'
  | 'hosp_max';

export const CONSTRAINT_LABELS: Record<ConstraintKey, string> = {
  alt: 'Высота',
  t_jul_max: 'T° июля',
  t_jan_min: 'T° января',
  hum_max: 'Влажность',
  snow_min: 'Снежных дней',
  sunny_min: 'Солнечных дней',
  zones: 'Зона рельефа',
  pop_max: 'Население',
  hosp_max: 'До больницы',
};

function getActiveConstraints(f: Filters): ConstraintKey[] {
  const out: ConstraintKey[] = [];
  if (f.alt_min !== F_DEFAULTS.alt_min || f.alt_max !== F_DEFAULTS.alt_max) out.push('alt');
  if (f.t_jul_max !== null) out.push('t_jul_max');
  if (f.t_jan_min !== null) out.push('t_jan_min');
  if (f.hum_max !== null) out.push('hum_max');
  if (f.snow_min !== null) out.push('snow_min');
  if (f.sunny_min !== null) out.push('sunny_min');
  if (f.zones.size > 0) out.push('zones');
  if (f.pop_max !== null) out.push('pop_max');
  if (f.hosp_max !== null) out.push('hosp_max');
  return out;
}

// Применить только указанный constraint, остальные — defaults
function applyOnlyConstraint(f: Filters, key: ConstraintKey): Filters {
  const base: Filters = { ...F_DEFAULTS, zones: new Set() };
  switch (key) {
    case 'alt':
      return { ...base, alt_min: f.alt_min, alt_max: f.alt_max };
    case 't_jul_max':
      return { ...base, t_jul_max: f.t_jul_max };
    case 't_jan_min':
      return { ...base, t_jan_min: f.t_jan_min };
    case 'hum_max':
      return { ...base, hum_max: f.hum_max };
    case 'snow_min':
      return { ...base, snow_min: f.snow_min };
    case 'sunny_min':
      return { ...base, sunny_min: f.sunny_min };
    case 'zones':
      return { ...base, zones: f.zones };
    case 'pop_max':
      return { ...base, pop_max: f.pop_max };
    case 'hosp_max':
      return { ...base, hosp_max: f.hosp_max };
  }
}

export interface TopConstraint {
  key: ConstraintKey;
  label: string;
  ratio: number; // 0..1 — доля comuni отсечённых только этим фильтром
}

/**
 * Для каждого активного фильтра считаем долю отсечённых comuni если бы
 * был только он. Возвращаем самый «жёсткий».
 */
export function detectTopConstraint(
  filters: Filters,
  comuni: ComuneFull[],
): TopConstraint | null {
  const total = comuni.length;
  if (total === 0) return null;

  const active = getActiveConstraints(filters);
  if (active.length === 0) return null;

  const scores = active.map((key) => {
    const isolated = applyOnlyConstraint(filters, key);
    let passing = 0;
    for (const c of comuni) if (matches(c, isolated)) passing++;
    return { key, ratio: (total - passing) / total };
  });

  scores.sort((a, b) => b.ratio - a.ratio);
  const top = scores[0];
  // меньше 10% — даже сам по себе слабый, не виноват
  if (top.ratio < 0.1) return null;
  return { key: top.key, label: CONSTRAINT_LABELS[top.key], ratio: top.ratio };
}

/**
 * Сбросить указанный constraint в default-значения. Возвращает partial для store.set().
 */
export function relaxPatch(key: ConstraintKey): Partial<Filters> {
  switch (key) {
    case 'alt':
      return { alt_min: F_DEFAULTS.alt_min, alt_max: F_DEFAULTS.alt_max };
    case 't_jul_max':
      return { t_jul_max: null };
    case 't_jan_min':
      return { t_jan_min: null };
    case 'hum_max':
      return { hum_max: null };
    case 'snow_min':
      return { snow_min: null };
    case 'sunny_min':
      return { sunny_min: null };
    case 'zones':
      return { zones: new Set() };
    case 'pop_max':
      return { pop_max: null };
    case 'hosp_max':
      return { hosp_max: null };
  }
}
